import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
    Search, Users, Building2, Trash2, UserPlus, CheckCircle2,
    AlertTriangle, Loader2, Sparkles, Wand2
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import SortableHeader, { useTableSort, useColumnFilters, usePagination, TablePagination } from '../table/SortableHeader';
import EditableCell from './EditableCell';
import EnrichmentModal from '../EnrichmentModal';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ── Column definitions ──────────────────────────────────────────────────────

// Column labels aligned with the canonical set across Contacts / Companies /
// List Management (tidy-up 2026-04-22).
const CONTACT_COLUMNS = [
    { key: 'first_name', label: 'First Name', group: 'contact' },
    { key: 'last_name', label: 'Last Name', group: 'contact' },
    { key: 'personal_email', label: 'Personal Email', group: 'contact' },
    { key: 'mobile_whatsapp', label: 'Mobile/WhatsApp', group: 'contact' },
    { key: 'job_title', label: 'Job Title', group: 'contact' },
    { key: 'department', label: 'Department', group: 'contact' },
    { key: 'seniority', label: 'Seniority', group: 'contact' },
    { key: 'role_in_deal', label: 'Role in Deal', group: 'contact' },
    { key: 'relationship', label: 'Relationship', group: 'contact' },
    { key: 'contact_city', label: 'City', group: 'contact' },
    { key: 'contact_county', label: 'County', group: 'contact' },
    { key: 'personal_linkedin_url', label: 'Personal LinkedIn URL', group: 'contact' },
    { key: 'source_tag', label: 'Source Tag', group: 'contact' },
    { key: 'status_event', label: 'Status Event', group: 'contact' },
    // v2.16.43 — Tags column missed StagingTable in v2.16.37's promotion
    // (it landed in the data path but not the table). Stored as a CSV
    // string on staging records (e.g. "VIP, Apr Webinar"); the commit
    // code's _parse_tags_cell splits to contact.tags on the way in.
    { key: 'tags', label: 'Tags', group: 'contact' },
];

const COMPANY_COLUMNS = [
    // v2.16.43: Neleke flagged the column header showed just "Company" —
    // canonical label is "Company Name" everywhere else (Companies grid,
    // Import Wizard, ContactDetail). Aligning here.
    { key: 'company_name', label: 'Company Name', group: 'company' },
    { key: 'company_size', label: 'Company Size', group: 'company' },
    { key: 'company_email', label: 'Company Email', group: 'company' },
    { key: 'company_phone', label: 'Company Phone', group: 'company' },
    { key: 'company_city', label: 'Company City', group: 'company' },
    { key: 'company_state', label: 'Company State/Province', group: 'company' },
    { key: 'company_country', label: 'Company Country', group: 'company' },
    { key: 'company_hq_country', label: 'Company HQ Country', group: 'company' },
    { key: 'company_linkedin_url', label: 'Company LinkedIn URL', group: 'company' },
    { key: 'company_website', label: 'Website URL', group: 'company' },
];

const ASSIGNMENT_COLUMNS = [
    { key: 'assigned_to_name', label: 'Owner', group: 'assignment' },
    { key: 'status', label: 'Status', group: 'assignment' },
];

const ALL_COLUMNS = [...CONTACT_COLUMNS, ...COMPANY_COLUMNS, ...ASSIGNMENT_COLUMNS];

// Default visible columns — tuned for the Marketing workflow (tidy-up
// 2026-04-22). Users can still toggle any off via the column picker.
const DEFAULT_VISIBLE = new Set([
    'first_name', 'last_name', 'personal_email', 'mobile_whatsapp', 'personal_linkedin_url',
    'job_title', 'seniority',
    'source_tag',
    // v2.16.43 — Neleke's request: surface Tags + Company HQ Country +
    // Company State/Province in the default view so records can be filtered
    // by those without manual cross-checking.
    'tags',
    'company_name', 'company_country', 'company_state', 'company_hq_country', 'company_city',
    'company_email', 'company_phone', 'company_website', 'company_linkedin_url',
    'assigned_to_name', 'status',
]);

// ── Component ───────────────────────────────────────────────────────────────

export default function StagingTable({
    importId,
    getAuthHeader,
    salesReps = [],
    onSelectionChange,
    onDataChange,
    geoFilters = {},
    repFilter = '',
}) {
    const [records, setRecords] = useState([]);
    const [loading, setLoading] = useState(true);
    const [totalRecords, setTotalRecords] = useState(0);
    const [search, setSearch] = useState('');
    const [selectedIds, setSelectedIds] = useState([]);
    const [visibleColumns, setVisibleColumns] = useState(DEFAULT_VISIBLE);
    const [showColumnPicker, setShowColumnPicker] = useState(false);

    // Bulk assign state
    const [showBulkAssign, setShowBulkAssign] = useState(false);
    const [bulkAssignRep, setBulkAssignRep] = useState('');
    const [assigning, setAssigning] = useState(false);

    // Enrichment
    const [showEnrichModal, setShowEnrichModal] = useState(false);

    // AI assignment suggestions
    const [showAISuggest, setShowAISuggest] = useState(false);
    const [aiSuggestions, setAiSuggestions] = useState([]);
    const [aiSuggestLoading, setAiSuggestLoading] = useState(false);

    // Load records
    const loadRecords = useCallback(async () => {
        try {
            setLoading(true);
            const params = { page_size: 5000 };
            if (importId) params.import_id = importId;
            if (search) params.search = search;
            if (geoFilters.region) params.region = geoFilters.region;
            if (geoFilters.country) params.company_country = geoFilters.country;
            if (geoFilters.state) params.company_state = geoFilters.state;
            if (repFilter) params.assigned_to = repFilter;

            const res = await axios.get(`${API}/staging/records`, {
                headers: getAuthHeader(), params
            });
            const data = res.data?.data || [];
            setRecords(data);
            setTotalRecords(res.data?.total || data.length);
        } catch (err) {
            toast.error('Failed to load staging records');
        } finally {
            setLoading(false);
        }
    }, [getAuthHeader, importId, search, geoFilters, repFilter]);

    useEffect(() => { loadRecords(); }, [loadRecords]);

    // Detect extra columns from data
    const extraColumns = useMemo(() => {
        const extras = new Set();
        records.forEach(rec => {
            if (rec.extra_fields) {
                Object.keys(rec.extra_fields).forEach(k => extras.add(k));
            }
        });
        return [...extras].map(k => ({ key: `extra_${k}`, label: k, group: 'extra', extraKey: k }));
    }, [records]);

    // Flatten extra_fields into rows for sorting/filtering
    const flatRecords = useMemo(() => {
        return records.map(rec => {
            const flat = { ...rec };
            if (rec.extra_fields) {
                Object.entries(rec.extra_fields).forEach(([k, v]) => {
                    flat[`extra_${k}`] = v;
                });
            }
            return flat;
        });
    }, [records]);

    // Column filter + sort + pagination
    const allCols = useMemo(() => [...ALL_COLUMNS, ...extraColumns], [extraColumns]);
    const { columnFilters, setColumnFilter, filteredData: columnFiltered } = useColumnFilters(flatRecords);
    const { sortColumn, sortDirection, handleSort, sortedData } = useTableSort(columnFiltered);
    const { page, pageSize, setPage, setPageSize, pageData, totalPages, total } = usePagination(sortedData, 100);

    // Selection
    const toggleSelectAll = () => {
        if (selectedIds.length === pageData.length) {
            setSelectedIds([]);
        } else {
            setSelectedIds(pageData.map(r => r.record_id));
        }
    };

    const toggleSelect = (id) => {
        setSelectedIds(prev =>
            prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
        );
    };

    useEffect(() => {
        onSelectionChange?.(selectedIds);
    }, [selectedIds, onSelectionChange]);

    // Inline edit handler
    const handleCellSave = async (recordId, field, value) => {
        // Optimistic update
        setRecords(prev => prev.map(r =>
            r.record_id === recordId ? { ...r, [field]: value } : r
        ));

        try {
            await axios.put(`${API}/staging/records/${recordId}`, {
                field, value
            }, { headers: getAuthHeader() });
        } catch (err) {
            toast.error('Failed to save edit');
            loadRecords(); // Revert
        }
    };

    // Bulk assign
    const handleBulkAssign = async () => {
        if (!bulkAssignRep || selectedIds.length === 0) return;
        setAssigning(true);
        try {
            const res = await axios.put(`${API}/staging/records/bulk-assign`, {
                record_ids: selectedIds,
                assign_to_user_id: bulkAssignRep,
            }, { headers: getAuthHeader() });

            toast.success(`Assigned ${res.data.assigned} records to ${res.data.assigned_to}`);
            setSelectedIds([]);
            setShowBulkAssign(false);
            setBulkAssignRep('');
            loadRecords();
            onDataChange?.();
        } catch (err) {
            const detail = err.response?.data?.detail || err.response?.data?.message || err.message || 'Assignment failed';
            toast.error(`Assignment failed: ${detail}`);
            console.error('Bulk assign error:', err.response?.status, err.response?.data);
        } finally {
            setAssigning(false);
        }
    };

    // Bulk delete
    const handleBulkDelete = async () => {
        if (selectedIds.length === 0) return;
        try {
            await axios.delete(`${API}/staging/records`, {
                headers: getAuthHeader(),
                data: { record_ids: selectedIds }
            });
            toast.success(`Deleted ${selectedIds.length} records`);
            setSelectedIds([]);
            loadRecords();
            onDataChange?.();
        } catch (err) {
            toast.error('Delete failed');
        }
    };

    // AI assignment suggestions
    const handleAISuggest = async () => {
        if (selectedIds.length === 0) return;
        setAiSuggestLoading(true);
        setShowAISuggest(true);
        try {
            const res = await axios.post(`${API}/staging/suggest-assignment`, {
                record_ids: selectedIds,
            }, { headers: getAuthHeader() });
            setAiSuggestions(res.data.suggestions || []);
        } catch (err) {
            toast.error('Failed to get AI suggestions');
            setShowAISuggest(false);
        } finally {
            setAiSuggestLoading(false);
        }
    };

    const handleAcceptSuggestion = async (recordIds, userId, repName) => {
        try {
            const res = await axios.put(`${API}/staging/records/bulk-assign`, {
                record_ids: recordIds,
                assign_to_user_id: userId,
            }, { headers: getAuthHeader() });
            toast.success(`Assigned ${res.data.assigned} records to ${repName}`);
            // Remove this suggestion from the list
            setAiSuggestions(prev => prev.filter(s =>
                !s.record_ids.every(id => recordIds.includes(id))
            ));
            loadRecords();
            onDataChange?.();
        } catch (err) {
            toast.error('Assignment failed');
        }
    };

    // Visible columns for rendering
    const displayColumns = allCols.filter(c => visibleColumns.has(c.key));

    // Status badge
    const StatusBadge = ({ status }) => {
        const styles = {
            pending: 'bg-stone-100 text-stone-600',
            assigned: 'bg-blue-50 text-blue-700',
            committed: 'bg-green-50 text-green-700',
            error: 'bg-red-50 text-red-700',
        };
        return (
            <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${styles[status] || styles.pending}`}>
                {status}
            </span>
        );
    };

    // Company grouping — add subtle left border color per company
    const companyColors = useMemo(() => {
        const colors = [
            'border-l-blue-300', 'border-l-emerald-300', 'border-l-amber-300',
            'border-l-purple-300', 'border-l-pink-300', 'border-l-cyan-300',
            'border-l-orange-300', 'border-l-lime-300', 'border-l-indigo-300',
        ];
        const map = {};
        let idx = 0;
        pageData.forEach(r => {
            const cn = (r.company_name || '').toLowerCase();
            if (cn && !map[cn]) {
                map[cn] = colors[idx % colors.length];
                idx++;
            }
        });
        return map;
    }, [pageData]);

    if (loading) {
        return (
            <div className="flex items-center justify-center py-12 text-stone-400">
                <Loader2 className="w-5 h-5 animate-spin mr-2" />
                Loading staging records...
            </div>
        );
    }

    return (
        <div className="flex flex-col gap-3">
            {/* ── Toolbar ── */}
            <div className="flex items-center gap-2 flex-wrap">
                {/* Search */}
                <div className="relative flex-1 min-w-[200px] max-w-sm">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
                    <Input
                        placeholder="Search contacts or companies..."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        className="pl-9"
                    />
                </div>

                {/* Column picker toggle */}
                <Button variant="outline" size="sm" onClick={() => setShowColumnPicker(!showColumnPicker)}>
                    Columns ({visibleColumns.size})
                </Button>

                {/* Record count */}
                <span className="text-sm text-stone-400 ml-auto">{totalRecords} records</span>

                {/* Bulk actions */}
                {selectedIds.length > 0 && (
                    <div className="flex items-center gap-2 ml-2 pl-2 border-l border-stone-200">
                        <span className="text-sm font-medium text-stone-700">{selectedIds.length} selected</span>
                        <Button size="sm" variant="outline" onClick={handleAISuggest}>
                            <Wand2 className="w-3.5 h-3.5 mr-1" />AI Assign
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => setShowBulkAssign(true)}>
                            <UserPlus className="w-3.5 h-3.5 mr-1" />Assign
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => setShowEnrichModal(true)}>
                            <Sparkles className="w-3.5 h-3.5 mr-1" />Enrich
                        </Button>
                        <Button size="sm" variant="outline" className="text-red-600 hover:text-red-700" onClick={handleBulkDelete}>
                            <Trash2 className="w-3.5 h-3.5 mr-1" />Delete
                        </Button>
                    </div>
                )}
            </div>

            {/* ── Column picker ── */}
            {showColumnPicker && (
                <div className="bg-stone-50 border border-stone-200 rounded-lg p-3">
                    <div className="flex items-center justify-between mb-2">
                        <span className="text-xs font-semibold text-stone-600 uppercase">Visible Columns</span>
                        <div className="flex gap-2">
                            <button
                                className="text-xs text-stone-500 hover:text-stone-700"
                                onClick={() => setVisibleColumns(new Set(allCols.map(c => c.key)))}
                            >Show All</button>
                            <button
                                className="text-xs text-stone-500 hover:text-stone-700"
                                onClick={() => setVisibleColumns(new Set(DEFAULT_VISIBLE))}
                            >Reset</button>
                        </div>
                    </div>
                    <div className="flex flex-wrap gap-1.5">
                        {allCols.map(col => (
                            <button
                                key={col.key}
                                onClick={() => {
                                    const next = new Set(visibleColumns);
                                    if (next.has(col.key)) next.delete(col.key);
                                    else next.add(col.key);
                                    setVisibleColumns(next);
                                }}
                                className={`text-xs px-2 py-1 rounded-full border transition-colors ${
                                    visibleColumns.has(col.key)
                                        ? col.group === 'contact' ? 'bg-blue-50 border-blue-200 text-blue-700'
                                        : col.group === 'company' ? 'bg-emerald-50 border-emerald-200 text-emerald-700'
                                        : col.group === 'extra' ? 'bg-purple-50 border-purple-200 text-purple-700'
                                        : 'bg-stone-200 border-stone-300 text-stone-700'
                                        : 'bg-white border-stone-200 text-stone-400'
                                }`}
                            >
                                {col.label}
                            </button>
                        ))}
                    </div>
                </div>
            )}

            {/* ── Table ── */}
            <div className="border border-stone-200 rounded-lg overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead className="bg-stone-50 border-b border-stone-200 sticky top-0 z-10">
                            <tr>
                                <th className="px-3 py-2 w-10">
                                    <input
                                        type="checkbox"
                                        checked={selectedIds.length === pageData.length && pageData.length > 0}
                                        onChange={toggleSelectAll}
                                        className="rounded border-stone-300"
                                    />
                                </th>
                                {displayColumns.map(col => (
                                    <SortableHeader
                                        key={col.key}
                                        label={col.label}
                                        columnKey={col.key}
                                        sortColumn={sortColumn}
                                        sortDirection={sortDirection}
                                        onSort={handleSort}
                                        filterValue={columnFilters[col.key]}
                                        onFilter={setColumnFilter}
                                        allData={flatRecords}
                                        className="!px-3 !py-2 !text-[10px]"
                                    />
                                ))}
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-stone-100">
                            {pageData.length === 0 ? (
                                <tr>
                                    <td colSpan={displayColumns.length + 1} className="px-4 py-8 text-center text-stone-400">
                                        No staging records found
                                    </td>
                                </tr>
                            ) : (
                                pageData.map((row) => {
                                    const cn = (row.company_name || '').toLowerCase();
                                    const borderColor = companyColors[cn] || 'border-l-transparent';
                                    const isSelected = selectedIds.includes(row.record_id);

                                    return (
                                        <tr
                                            key={row.record_id}
                                            className={`hover:bg-stone-50 border-l-2 ${borderColor} ${
                                                isSelected ? 'bg-blue-50/50' : ''
                                            }`}
                                        >
                                            <td className="px-3 py-1.5">
                                                <input
                                                    type="checkbox"
                                                    checked={isSelected}
                                                    onChange={() => toggleSelect(row.record_id)}
                                                    className="rounded border-stone-300"
                                                />
                                            </td>
                                            {displayColumns.map(col => (
                                                <td key={col.key} className="px-3 py-1.5 text-stone-600">
                                                    {col.key === 'status' ? (
                                                        <StatusBadge status={row.status} />
                                                    ) : col.key === 'assigned_to_name' ? (
                                                        row.assigned_to_name ? (
                                                            <span className="flex items-center gap-1 text-blue-700">
                                                                <CheckCircle2 className="w-3 h-3" />
                                                                {row.assigned_to_name}
                                                            </span>
                                                        ) : (
                                                            <span className="text-amber-500 flex items-center gap-1">
                                                                <AlertTriangle className="w-3 h-3" />
                                                                Unassigned
                                                            </span>
                                                        )
                                                    ) : col.extraKey ? (
                                                        // Extra fields are read-only for now
                                                        <span>{row[col.key] || <span className="text-stone-300">—</span>}</span>
                                                    ) : (
                                                        <EditableCell
                                                            value={row[col.key]}
                                                            recordId={row.record_id}
                                                            fieldKey={col.key}
                                                            onSave={handleCellSave}
                                                        />
                                                    )}
                                                </td>
                                            ))}
                                        </tr>
                                    );
                                })
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* ── Pagination ── */}
            <TablePagination
                page={page}
                totalPages={totalPages}
                total={total}
                pageSize={pageSize}
                onPageChange={setPage}
                onPageSizeChange={setPageSize}
            />

            {/* ── Bulk Assign Modal ── */}
            {/* ── Bulk Assign Modal ── */}
            {showBulkAssign && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
                    <div className="bg-white rounded-xl shadow-xl p-6 w-full max-w-md">
                        <h3 className="text-lg font-semibold text-stone-900 mb-1">Assign Sales Rep</h3>
                        <p className="text-sm text-stone-500 mb-4">
                            Assign {selectedIds.length} record{selectedIds.length !== 1 ? 's' : ''} to a sales rep
                        </p>

                        <Select value={bulkAssignRep} onValueChange={setBulkAssignRep}>
                            <SelectTrigger>
                                <SelectValue placeholder="Select a sales rep..." />
                            </SelectTrigger>
                            <SelectContent>
                                {salesReps.map(rep => (
                                    <SelectItem key={rep.user_id} value={rep.user_id}>
                                        {rep.name} ({rep.email})
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>

                        <div className="flex items-center justify-end gap-2 mt-4">
                            <Button variant="outline" onClick={() => { setShowBulkAssign(false); setBulkAssignRep(''); }}>
                                Cancel
                            </Button>
                            <Button
                                onClick={handleBulkAssign}
                                disabled={!bulkAssignRep || assigning}
                                className="bg-stone-900 hover:bg-stone-800"
                            >
                                {assigning ? (
                                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Assigning...</>
                                ) : (
                                    <>Assign {selectedIds.length} Record{selectedIds.length !== 1 ? 's' : ''}</>
                                )}
                            </Button>
                        </div>
                    </div>
                </div>
            )}

            {/* ── Enrichment Modal ── */}
            <EnrichmentModal
                open={showEnrichModal}
                onOpenChange={setShowEnrichModal}
                recordType="staging"
                recordIds={selectedIds}
                onComplete={() => { loadRecords(); onDataChange?.(); }}
            />

            {/* ── AI Assignment Suggestions Modal ── */}
            {showAISuggest && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
                    <div className="bg-white rounded-xl shadow-xl p-6 w-full max-w-xl max-h-[80vh] overflow-y-auto">
                        <div className="flex items-center justify-between mb-4">
                            <div className="flex items-center gap-2">
                                <Wand2 className="w-5 h-5 text-amber-500" />
                                <h3 className="text-lg font-semibold text-stone-900">AI Assignment Suggestions</h3>
                            </div>
                            <button onClick={() => setShowAISuggest(false)} className="text-stone-400 hover:text-stone-600">
                                <span className="text-xl">&times;</span>
                            </button>
                        </div>

                        {aiSuggestLoading ? (
                            <div className="flex flex-col items-center justify-center py-12">
                                <Sparkles className="w-8 h-8 text-amber-400 animate-pulse mb-3" />
                                <p className="text-sm text-stone-500">AI is analyzing rep profiles and workloads...</p>
                                <p className="text-xs text-stone-400 mt-1">Matching {selectedIds.length} records to your sales team</p>
                            </div>
                        ) : aiSuggestions.length === 0 ? (
                            <div className="text-center py-8 text-stone-400">
                                <p className="text-sm">No suggestions available</p>
                                <Button variant="outline" size="sm" className="mt-3" onClick={() => { setShowAISuggest(false); setShowBulkAssign(true); }}>
                                    Assign Manually
                                </Button>
                            </div>
                        ) : (
                            <div className="space-y-4">
                                {aiSuggestions.map((suggestion, idx) => (
                                    <div key={idx} className="bg-stone-50 border border-stone-200 rounded-lg p-4">
                                        <div className="flex items-center justify-between mb-3">
                                            <div>
                                                <h5 className="text-sm font-semibold text-stone-900">{suggestion.company_name}</h5>
                                                <p className="text-xs text-stone-400">{suggestion.record_ids?.length || 0} contacts</p>
                                            </div>
                                        </div>
                                        <div className="space-y-2">
                                            {(suggestion.recommended_reps || []).map((rep, rIdx) => (
                                                <button
                                                    key={rep.user_id}
                                                    onClick={() => handleAcceptSuggestion(suggestion.record_ids, rep.user_id, rep.name)}
                                                    className="w-full flex items-center gap-3 p-3 rounded-lg border border-stone-100 bg-white hover:border-stone-300 hover:bg-stone-50 transition-colors text-left"
                                                >
                                                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold ${
                                                        rIdx === 0 ? 'bg-stone-900 text-white' : 'bg-stone-100 text-stone-600'
                                                    }`}>
                                                        {rep.name?.split(' ').map(n => n[0]).join('').slice(0, 2)}
                                                    </div>
                                                    <div className="flex-1 min-w-0">
                                                        <div className="flex items-center gap-2">
                                                            <span className="text-sm font-medium text-stone-900">{rep.name}</span>
                                                            {rIdx === 0 && (
                                                                <span className="text-xs px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700 font-medium">Best Match</span>
                                                            )}
                                                        </div>
                                                        <p className="text-xs text-stone-500 mt-0.5 truncate">{rep.reasoning}</p>
                                                    </div>
                                                    <span className={`text-xs px-2 py-1 rounded-full font-semibold border ${
                                                        rep.score >= 80 ? 'bg-emerald-100 text-emerald-700 border-emerald-200' :
                                                        rep.score >= 60 ? 'bg-amber-100 text-amber-700 border-amber-200' :
                                                        'bg-stone-100 text-stone-600 border-stone-200'
                                                    }`}>
                                                        {rep.score}%
                                                    </span>
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                ))}

                                <div className="flex items-center justify-between pt-2 border-t border-stone-200">
                                    <Button variant="outline" size="sm" onClick={() => { setShowAISuggest(false); setShowBulkAssign(true); }}>
                                        Skip — Assign Manually
                                    </Button>
                                    <Button variant="outline" size="sm" onClick={() => setShowAISuggest(false)}>
                                        Done
                                    </Button>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}
